import type { FleetDevice } from 'src/api/endpoints/cloudEnterprise.types';
import { ExpandableNotification } from 'src/components/ExpandableNotification';
import { TestId } from 'src/testids';
import { fleetMemberRowHelpers } from './fleetMemberRowHelpers';

export function DeviceComplianceNotification({
  device,
  darken = false,
}: {
  device: FleetDevice;
  darken?: boolean;
}) {
  const complianceStatus =
    fleetMemberRowHelpers.getDeviceComplianceStatus(device);

  switch (complianceStatus) {
    case 'nonCompliant':
      return (
        <ExpandableNotification
          data-cy={TestId.NotificationEnterpriseDeviceComplianceStatus}
          aria-label="Non-compliant with policy settings"
          level="error"
          title="Non-compliant with policy settings"
          className={darken ? 'bg-paper-4' : undefined}
        >
          This device does not meet current security policies. Ensure that it is
          connected to Wi-Fi and actively in use to complete a check-in. If
          recently paired or updated, policies may take up to an hour to apply.
        </ExpandableNotification>
      );
    case 'compliant':
      return null;
    case 'notApplicable':
      return (
        <ExpandableNotification
          data-cy={TestId.NotificationEnterpriseDeviceComplianceStatus}
          aria-label="Compliance status not applicable"
          level="info"
          title="Compliance status not applicable"
          className={darken ? 'bg-paper-4' : undefined}
        >
          Compliance status is not applicable for this device. It either does
          not support policies or has none assigned.
        </ExpandableNotification>
      );
    case 'unknown':
    default:
      return (
        <ExpandableNotification
          data-cy={TestId.NotificationEnterpriseDeviceComplianceStatus}
          aria-label="Compliance status unavailable"
          level="warning"
          title="Compliance status unavailable"
          className={darken ? 'bg-paper-4' : undefined}
        >
          The system can&apos;t currently determine the compliance status of
          this device. This may be a connection or system error. Check that the
          device is connected to Wi-Fi and running the latest software, or try
          restarting it.
        </ExpandableNotification>
      );
  }
}
